import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../shared/TailwindComponents/Button';
import { PiSpinnerGapLight, PiTrashLight, PiRecycleFill } from 'react-icons/pi';

interface RecycleBinDeleteConfirmationProps {
  itemCount: number;
  isEmptyAll?: boolean;
  onConfirm: () => Promise<void> | void;
  onCancel: () => void;
}

const RecycleBinDeleteConfirmation: React.FC<RecycleBinDeleteConfirmationProps> = ({
  itemCount,
  isEmptyAll = false,
  onConfirm,
  onCancel,
}) => {
  const { t } = useTranslation();
  const [isDeleting, setIsDeleting] = useState(false);

  const handleConfirm = async () => {
    setIsDeleting(true);
    try {
      await onConfirm();
    } catch {
      setIsDeleting(false);
    }
  };

  if (isDeleting) {
    return (
      <div className="flex flex-col items-center justify-center py-10 px-4 gap-4">
        <div className="relative w-12 h-12">
          <PiSpinnerGapLight className="w-12 h-12 text-red-500 animate-spin" />
        </div>
        <div className="text-center space-y-1.5">
          <p className="text-sm font-medium text-gray-900 dark:text-white">
            {isEmptyAll
              ? t('Emptying recycle bin...')
              : itemCount === 1
                ? t('Permanently deleting 1 item...')
                : t(`Permanently deleting ${itemCount} items...`)}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {t('Please wait, this may take a moment.')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="py-1 px-4">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {isEmptyAll
            ? t(`All ${itemCount} ${itemCount === 1 ? 'item' : 'items'} in the recycle bin will be permanently deleted. This action cannot be undone.`)
            : t(`${itemCount} ${itemCount === 1 ? 'item' : 'items'} will be permanently deleted. This action cannot be undone.`)}
        </p>
      </div>

      <div className="flex justify-end space-x-4 pt-6 mt-2 border-t border-gray-300 dark:border-gray-700 px-4">
        <Button variant="neutral" size="small" onClick={onCancel} className="w-24">
          {t('Cancel')}
        </Button>
        <Button variant="destructive" size="small" onClick={handleConfirm} className="w-24">
          {t('Delete')}
        </Button>
      </div>
    </div>
  );
};

export default RecycleBinDeleteConfirmation;
